// "Routines" are marketed as "Agents" in the UI; the code name remains "routines".
import {getTestProps} from '@benchling/js-test-utils';
import {Box, Card, createStyles, Flex, Group, Loader, Stack, Text, Title} from '@mantine/core';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {CHAT_CONTENT_MAX_WIDTH_PX} from 'llm/chat-library/ui/AIStyleConstants';
import {CenteredSpinner} from 'ui-library/Spinner';
import fuzzydate from 'util/fuzzydate';

import RoutineDmChatInput from './RoutineDmChatInput';
import {RoutineRunsList} from './RoutineRunsTab';
import useAgentHomepageRoutineExecutions from './useAgentHomepageRoutineExecutions';
import useRoutine from './useRoutine';

interface AgentHomepageProps {
  routineId: string;
}

const useStyles = createStyles((theme) => ({
  // Fill the (bounded, scrolling) sidepanel content slot so the composer can pin to its bottom
  // rather than sitting at the end of the page's scroll.
  root: {
    flex: 1,
    minHeight: 0,
  },
  // The Overview + Agent activity scroll here; the composer footer stays put below it.
  scrollRegion: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  // Pinned composer bar. No divider: the composer sits flush against the activity list above it.
  footer: {
    flexShrink: 0,
    backgroundColor: theme.white,
  },
  overviewContainer: {
    maxWidth: CHAT_CONTENT_MAX_WIDTH_PX,
  },
}));

export default function AgentHomepage({routineId}: AgentHomepageProps): JSX.Element | null {
  const {classes} = useStyles();
  const {llmEnableRoutineHomepage} = useRolloutFlags();
  const homepageEnabled = llmEnableRoutineHomepage === true;
  const directMessagingEnabled = BenchlingConfig.LLM_ENABLE_DIRECT_AGENT_RUNS;

  const {routine, isLoading, isError} = useRoutine({routineId, enabled: homepageEnabled});

  const {
    total,
    runs,
    isLoading: executionsLoading,
    isError: executionsError,
    hasMore,
    loadMore,
  } = useAgentHomepageRoutineExecutions({
    routineId,
    enabled: homepageEnabled && routine != null,
  });

  if (!homepageEnabled) {
    return null;
  }

  if (isLoading) {
    return <CenteredSpinner />;
  }

  if (isError) {
    return (
      <Box
        className={classes.overviewContainer}
        mx="auto"
        px="md"
        py="lg"
        w="100%"
        {...getTestProps({component: 'AgentHomepage', element: 'root'})}
      >
        <Text size="sm" c="dimmed">
          Failed to load this Agent. Please try again.
        </Text>
      </Box>
    );
  }

  if (routine == null) {
    return (
      <Box
        className={classes.overviewContainer}
        mx="auto"
        px="md"
        py="lg"
        w="100%"
        {...getTestProps({component: 'AgentHomepage', element: 'root'})}
      >
        <Text size="sm" c="dimmed">
          This Agent could not be found.
        </Text>
      </Box>
    );
  }

  const name = routine.name ?? 'Agent';
  const {triggerType} = routine;
  // Executions come back newest-first, so the first item is the most recent run. Only surface the
  // stats once the executions query has resolved successfully; while it is pending or errored, the
  // total and newest run are unknown rather than genuinely 0 / absent.
  const newestRun = !executionsLoading && !executionsError && runs.length > 0 ? runs[0] : null;

  const totalRuns = executionsLoading ? (
    <Loader size="sm" aria-label="Loading total runs" />
  ) : executionsError ? (
    <Text size="sm" c="dimmed">
      Unable to load
    </Text>
  ) : (
    <Text size="xl" fw={700}>
      {total}
    </Text>
  );

  return (
    <Flex
      direction="column"
      className={classes.root}
      {...getTestProps({component: 'AgentHomepage', element: 'root'})}
    >
      <Box className={classes.scrollRegion}>
        <Box className={classes.overviewContainer} mx="auto" px="md" py="lg" w="100%">
          <Stack spacing="lg">
            <Group spacing="xs" align="baseline">
              <Title order={2}>Overview</Title>
              {newestRun != null && (
                <Text size="sm" c="dimmed">
                  Last run {fuzzydate(newestRun.createdAt)}
                </Text>
              )}
            </Group>

            <Card withBorder={true} radius="md" padding="md">
              <Text size="sm" c="dimmed">
                Total runs
              </Text>
              {totalRuns}
            </Card>

            <Stack spacing="sm">
              <Title order={3}>Agent activity</Title>
              <RoutineRunsList
                runs={runs}
                isLoading={executionsLoading}
                isError={executionsError}
                hasMore={hasMore}
                loadMore={loadMore}
                routineName={name}
                showExecutionKind={true}
                triggerType={triggerType}
              />
            </Stack>
          </Stack>
        </Box>
      </Box>

      {directMessagingEnabled && (
        <Box className={classes.footer}>
          <Box className={classes.overviewContainer} mx="auto" px="md" py="md" w="100%">
            <RoutineDmChatInput routineId={routineId} />
          </Box>
        </Box>
      )}
    </Flex>
  );
}
